/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { FiTrash2 } from "react-icons/fi";
import { LuFileCode } from "react-icons/lu";
import { Button, KIND, SHAPE, SIZE } from "baseui/button";
import { Block } from "baseui/block";
import { TableBuilder, TableBuilderColumn } from "baseui/table-semantic";
import { Select } from "baseui/select";
import { FileInfo } from "Api";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { TOP_ITEM_ZINDEX } from "../../Utils/SiteProps";
import { mbsSizeAsText } from "../../Api/ApiSerialization";
import { TYPE_OPTIONS } from "../../Utils/Common";

/** Step 2 of the populate modal: confirm or change each file's type. */

const VERIFY_TABLE_MAX_HEIGHT = 300;

export const VerifyFileTable = ({
  files,
  onTypeChange,
  onRemove,
  maxHeight = VERIFY_TABLE_MAX_HEIGHT,
}: {
  files: FileInfo[];
  onTypeChange: (fileId: string, fileType: string) => void;
  onRemove: (fileId: string) => void;
  /** Max height of the scrollable table, in px. */
  maxHeight?: number;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const { colors } = currentTheme;
  const divider = `1px solid ${colors.dropdownBorderColor}`;

  const selectOptions = React.useMemo(
    () =>
      TYPE_OPTIONS.map((option) => ({
        id: option.id,
        label: option.label ?? option.id,
      })),
    []
  );

  // Same head and body cell styles as FileSearchResultTable, on the modal's
  // background. The header sticks to the top while the rows scroll.
  const headCellStyle = React.useMemo(
    () => ({
      position: "sticky" as const,
      top: 0,
      zIndex: 1,
      backgroundColor: colors.pageBackgroundColor,
      color: colors.secondaryTextColor,
      fontFamily: currentTheme.fonts.primary,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "16px",
      paddingTop: "12px",
      paddingBottom: "12px",
      paddingLeft: "12px",
      paddingRight: "12px",
      borderBottom: divider,
      borderRight: "none",
      whiteSpace: "nowrap" as const,
      "::before": { display: "none" },
      "::after": { display: "none" },
    }),
    [colors, currentTheme.fonts.primary, divider]
  );

  const bodyCellStyle = React.useMemo(
    () => ({
      height: "46px",
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: "12px",
      paddingRight: "12px",
      verticalAlign: "middle" as const,
      backgroundColor: "transparent",
      color: colors.tableTextColor,
      fontFamily: currentTheme.fonts.primary,
      fontSize: "14px",
      whiteSpace: "nowrap" as const,
      borderTop: "none",
      borderRight: "none",
      borderBottom: divider,
    }),
    [colors, currentTheme.fonts.primary, divider]
  );

  type ColumnOverrides = React.ComponentProps<
    typeof TableBuilderColumn
  >["overrides"];

  // Column overrides replace the table-level cell styles, so re-apply them
  // and add the fixed width.
  const fixedWidth = (width: string, extra: object = {}): ColumnOverrides => ({
    TableHeadCell: { style: { ...headCellStyle, width, ...extra } },
    TableBodyCell: { style: { ...bodyCellStyle, width, ...extra } },
  });

  const selectOverrides = React.useMemo(
    () => ({
      Root: { style: { width: "100%" } },
      ControlContainer: {
        style: {
          backgroundColor: colors.dropdownBackground,
          borderTopWidth: "1px",
          borderBottomWidth: "1px",
          borderLeftWidth: "1px",
          borderRightWidth: "1px",
          borderTopStyle: "solid",
          borderBottomStyle: "solid",
          borderLeftStyle: "solid",
          borderRightStyle: "solid",
          borderTopColor: colors.dropdownBorderColor,
          borderBottomColor: colors.dropdownBorderColor,
          borderLeftColor: colors.dropdownBorderColor,
          borderRightColor: colors.dropdownBorderColor,
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
          borderBottomLeftRadius: "6px",
          borderBottomRightRadius: "6px",
          cursor: "pointer",
        },
      },
      SingleValue: {
        style: {
          color: colors.defaultTextColor,
          fontFamily: currentTheme.fonts.primary,
          fontSize: currentTheme.fontSizes.medium,
        },
      },
      SelectArrow: { style: { color: colors.secondaryTextColor } },
      Dropdown: { style: { backgroundColor: colors.dropdownBackground } },
      DropdownListItem: {
        style: ({ $isHighlighted }: { $isHighlighted?: boolean }) => ({
          backgroundColor: $isHighlighted
            ? colors.dropdownItemHoverBackground
            : colors.dropdownBackground,
          color: colors.defaultTextColor,
          fontFamily: currentTheme.fonts.primary,
          fontSize: currentTheme.fontSizes.medium,
        }),
      },
      // The list opens in a layer, so lift it above the modal.
      Popover: {
        props: {
          overrides: { Body: { style: { zIndex: TOP_ITEM_ZINDEX } } },
        },
      },
    }),
    [colors, currentTheme.fonts.primary, currentTheme.fontSizes.medium]
  );

  return (
    <TableBuilder
      data={files}
      overrides={{
        Root: {
          style: {
            maxHeight: `${maxHeight}px`,
            overflowY: "auto",
            backgroundColor: colors.pageBackgroundColor,
          },
        },
        Table: {
          style: {
            width: "100%",
            tableLayout: "fixed",
            backgroundColor: colors.pageBackgroundColor,
          },
        },
        TableHeadRow: {
          style: { backgroundColor: colors.pageBackgroundColor },
        },
        TableHeadCell: { style: headCellStyle },
        TableBodyRow: {
          style: {
            backgroundColor: colors.pageBackgroundColor,
            ":hover": { backgroundColor: colors.dropdownItemHoverBackground },
          },
        },
        TableBodyCell: { style: bodyCellStyle },
        TableEmptyMessage: {
          style: { backgroundColor: colors.pageBackgroundColor },
        },
      }}
      emptyMessage={
        <Block
          paddingTop="48px"
          paddingBottom="48px"
          overrides={{
            Block: {
              style: {
                textAlign: "center",
                color: colors.secondaryTextColor,
                fontFamily: currentTheme.fonts.primary,
                fontSize: "14px",
              },
            },
          }}
        >
          No files. Go back to upload files to generate input nodes.
        </Block>
      }
    >
      <TableBuilderColumn
        id="icon"
        header=""
        overrides={fixedWidth("16px", { paddingRight: "0px" })}
      >
        {() => <LuFileCode size={16} color={colors.secondaryTextColor} />}
      </TableBuilderColumn>

      <TableBuilderColumn id="name" header="File name">
        {(file: FileInfo) => (
          <Block
            title={file.attributes?.name}
            overrides={{
              Block: {
                style: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: colors.defaultTextColor,
                },
              },
            }}
          >
            {file.attributes?.name}
          </Block>
        )}
      </TableBuilderColumn>

      <TableBuilderColumn
        id="size"
        header="Size"
        numeric
        overrides={fixedWidth("80px", { textAlign: "right" })}
      >
        {(file: FileInfo) => (
          <Block
            overrides={{
              Block: { style: { color: colors.secondaryTextColor } },
            }}
          >
            {mbsSizeAsText(file.attributes?.sizeMbs)}
          </Block>
        )}
      </TableBuilderColumn>

      <TableBuilderColumn
        id="fileType"
        header="File type"
        overrides={fixedWidth("280px")}
      >
        {(file: FileInfo) => (
          <Select
            size="compact"
            aria-label={`File type for ${file.attributes?.name}`}
            options={selectOptions}
            value={selectOptions.filter(
              (option) => option.id === file.userSelectedFileType
            )}
            labelKey="label"
            valueKey="id"
            clearable={false}
            searchable={false}
            onChange={({ value }) => {
              const next = value[0]?.id;
              if (next !== undefined) {
                onTypeChange(file.id, String(next));
              }
            }}
            overrides={selectOverrides}
          />
        )}
      </TableBuilderColumn>

      <TableBuilderColumn
        id="remove"
        header=""
        overrides={fixedWidth("32px", { paddingLeft: "4px" })}
      >
        {(file: FileInfo) => (
          <Button
            kind={KIND.tertiary}
            size={SIZE.compact}
            shape={SHAPE.square}
            aria-label={`Remove ${file.attributes?.name}`}
            title="Remove"
            onClick={() => onRemove(file.id)}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: "transparent",
                  color: colors.secondaryTextColor,
                  ":hover": {
                    backgroundColor: colors.dropdownItemHoverBackground,
                    color: colors.negative,
                  },
                },
              },
            }}
          >
            <FiTrash2 size={16} />
          </Button>
        )}
      </TableBuilderColumn>
    </TableBuilder>
  );
};
